/** Generator Status Review labels and defaults shared by the UI, SQL read path, and CSV. */

/** Hide short periods below reported status unless direction or consecutive months is explicitly filtered. */
export const DOWNWARD_MIN_SUSTAINED_MONTHS = 12;

/** Filter-only omnisearch key; `true` shows the shorter periods without narrowing the grid. */
export const INCLUDE_SHORT_BELOW_REPORTED_KEY = "includeShortBelowReported";

/**
 * Filter keys (with aliases) owned by each group of stat cards. A card's count ignores
 * its group's filters and its link replaces them, so the count matches the click-through.
 */
export const STAT_CARD_FILTER_KEYS = {
  comparison: ["type", "direction", "combinedCalculatedStatus", "sustained", "sustainedMonths"],
  queue: ["stage", "lifecycle", "status", "resolutionStatus", "resolution", "effectiveStatus"],
  risk: ["risk", "riskLevel"],
} as const satisfies Record<string, readonly string[]>;

/** Risk uses the span between affected months, including gaps, not elapsed time awaiting review. */
export const RISK_ELEVATED_MIN_RUN_MONTHS = 3;
export const RISK_HIGH_MIN_RUN_MONTHS = 6;

/** Stable filter/URL keys; display text is in EXCEEDANCE_STAGE_LABELS. */
export const EXCEEDANCE_STAGE_IDS = [
  "AWAITING_VALIDATION",
  "READY_FOR_PICKUP",
  "IN_FLIGHT",
  "AWAITING_CORRECTION",
  "AWAITING_EPA_UPDATE",
  "RESOLVED",
  "IGNORED",
] as const;

export type ExceedanceStage = (typeof EXCEEDANCE_STAGE_IDS)[number];

/** Review status labels shared by cards, filters, sorting, chips, and exports. */
export const EXCEEDANCE_STAGE_LABELS: Record<ExceedanceStage, string> = {
  AWAITING_VALIDATION: "Needs validation",
  READY_FOR_PICKUP: "Ready for pickup",
  IN_FLIGHT: "Submission in progress",
  AWAITING_CORRECTION: "Awaiting manifest correction",
  AWAITING_EPA_UPDATE: "Resolved – pending EPA update",
  RESOLVED: "Resolved",
  IGNORED: "Dismissed",
};

/** A resolved review still pending an EPA update after this many days is overdue. */
export const EPA_UPDATE_OVERDUE_DAYS = 14;

/** Same-month calculated versus reported comparisons, independent of validation. */
export const EXCEEDANCE_DIRECTION_LABELS: Record<"UPWARD" | "DOWNWARD", string> = {
  UPWARD: "Above reported status",
  DOWNWARD: "Below reported status",
};

/** Display labels for the derived compliance-risk signal. */
export const EXCEEDANCE_RISK_LABELS: Record<"ELEVATED" | "HIGH", string> = {
  ELEVATED: "Elevated",
  HIGH: "High",
};

/** Resolution-method labels; a linked submission may still be in progress. */
export const EXCEEDANCE_RESOLUTION_TYPE_LABELS: Record<
  "NOTIFICATION_FILED" | "ACKNOWLEDGED" | "DISMISSED" | "MANIFEST_CORRECTION",
  string
> = {
  NOTIFICATION_FILED: "Linked submission",
  ACKNOWLEDGED: "Corrected elsewhere",
  DISMISSED: "Dismissed",
  MANIFEST_CORRECTION: "Manifest correction",
};
